import { Button } from "@frontend/components/ui/button"
import { Card, CardContent } from "@frontend/components/ui/card"
import { DataTable } from "@frontend/components/ui/data-table"
import {
  useCreateInstanceTemplate,
  useDeleteInstanceTemplate,
  useInstanceTemplates,
  useUpdateInstanceTemplate
} from "@frontend/hooks/use-instance"
import {
  ModuleTemplateEditorDialog,
  type ModuleTemplateEditorInitialValue,
  type ModuleTemplateEditorSubmitPayload
} from "@frontend/pages/module-templates/components/module-template-editor-dialog"
import { formatModuleType } from "@frontend/pages/project-form/lib/utils"
import type { ColumnDef } from "@tanstack/react-table"
import { ArrowLeft, Pencil, Trash2 } from "lucide-react"
import { useCallback, useMemo, useState } from "react"
import { Link } from "react-router"

interface Template {
  id: string
  is_builtin: boolean
  name: string
  type: string
  schema: string
  version: number
  created_at: string
  updated_at: string
  usage_count: number
}

export function ModuleTemplatesPage() {
  const { data: result, isLoading } = useInstanceTemplates()
  const createTemplate = useCreateInstanceTemplate()
  const updateTemplate = useUpdateInstanceTemplate()
  const deleteTemplate = useDeleteInstanceTemplate()

  const [editingTemplate, setEditingTemplate] = useState<Template | null>(null)
  const [editorOpen, setEditorOpen] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string>()

  const templates: Template[] = result?.data ?? []
  const editorMode = editingTemplate ? "edit" : "create"

  const handleCreate = () => {
    setEditingTemplate(null)
    setErrorMessage(undefined)
    setEditorOpen(true)
  }

  const handleEdit = useCallback((template: Template) => {
    setEditingTemplate(template)
    setErrorMessage(undefined)
    setEditorOpen(true)
  }, [])

  const handleDelete = useCallback(
    (template: Template) => {
      if (confirm(`Delete template "${template.name}"? This cannot be undone.`)) {
        deleteTemplate.mutate(template.id)
      }
    },
    [deleteTemplate.mutate]
  )

  const handleSubmit = (payload: ModuleTemplateEditorSubmitPayload) => {
    setErrorMessage(undefined)
    const options = {
      onSuccess: () => setEditorOpen(false),
      onError: (err: Error) => setErrorMessage(err.message || "Failed to save template")
    }

    if (editingTemplate) {
      updateTemplate.mutate(
        { templateId: editingTemplate.id, name: payload.name, schema: payload.schema, version: payload.version },
        options
      )
    } else {
      createTemplate.mutate(payload, options)
    }
  }

  const handleOpenChange = (open: boolean) => {
    if (!open) setErrorMessage(undefined)
    setEditorOpen(open)
  }

  const initialValue: ModuleTemplateEditorInitialValue | undefined = editingTemplate
    ? {
        name: editingTemplate.name,
        type: editingTemplate.type,
        schema: editingTemplate.schema,
        version: editingTemplate.version
      }
    : undefined

  const columns = useMemo<ColumnDef<Template>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span className="text-sm font-medium">{row.original.name}</span>
      },
      {
        accessorKey: "type",
        header: "Type",
        cell: ({ row }) => (
          <span className="text-xs bg-secondary px-1.5 py-0.5 rounded">{formatModuleType(row.original.type)}</span>
        )
      },
      {
        accessorKey: "is_builtin",
        header: "Built-in",
        cell: ({ row }) =>
          row.original.is_builtin ? (
            <span className="text-xs bg-primary/10 text-primary px-1.5 py-0.5 rounded">Yes</span>
          ) : (
            <span className="text-xs text-muted-foreground">No</span>
          )
      },
      {
        accessorKey: "version",
        header: "Version",
        cell: ({ row }) => <span className="text-sm text-muted-foreground">v{row.original.version}</span>
      },
      {
        accessorKey: "usage_count",
        header: "Usage",
        cell: ({ row }) => (
          <span className="text-sm text-muted-foreground">
            {row.original.usage_count} {row.original.usage_count === 1 ? "project" : "projects"}
          </span>
        )
      },
      {
        id: "actions",
        header: "",
        cell: ({ row }) => {
          const t = row.original
          return (
            <div className="flex justify-end gap-1">
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={t.is_builtin}
                title={t.is_builtin ? "Built-in templates cannot be edited" : "Edit template"}
                onClick={() => handleEdit(t)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={t.is_builtin || t.usage_count > 0}
                title={
                  t.is_builtin
                    ? "Built-in templates cannot be deleted"
                    : t.usage_count > 0
                      ? "Cannot delete template in use"
                      : "Delete template"
                }
                onClick={() => handleDelete(t)}
              >
                <Trash2 className="h-4 w-4 text-destructive" />
              </Button>
            </div>
          )
        }
      }
    ],
    [handleDelete, handleEdit]
  )

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <p className="text-muted-foreground">Loading templates...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" asChild>
            <Link to="/">
              <ArrowLeft className="h-4 w-4" />
            </Link>
          </Button>
          <div>
            <h1>Module Templates</h1>
            <p className="text-muted-foreground text-[12px]">Instance</p>
          </div>
        </div>
        <Button size="sm" onClick={handleCreate}>
          Create Template
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto p-3">
            <DataTable columns={columns} data={templates} emptyMessage="No templates found." showPagination={false} />
          </div>
        </CardContent>
      </Card>

      <ModuleTemplateEditorDialog
        open={editorOpen}
        mode={editorMode}
        initialValue={initialValue}
        isPending={createTemplate.isPending || updateTemplate.isPending}
        errorMessage={errorMessage}
        onOpenChange={handleOpenChange}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
